<template>
  <nx-fullscreen-dialog
      :active="active"
      :title="$t('message.block.form.image.label')"
      @dismiss="dismiss">

    <template slot="toolbar-items">
      <v-btn
          :disabled="!isValid"
          @click="insertBlot"
          color="accent"
          flat
          v-html="$t('message.block.form.image.accept')"
      ></v-btn>
    </template>

    <v-container pb-3 fluid>
      <v-layout>
        <v-flex class="md8 offset-md2 sm10 offset-sm1 pt-4">
          <v-layout row wrap>

            <v-flex class="xs12">
              <h2 class="nx-block-headline" v-html="$t('message.block.form.title')"></h2>
              <v-select
                  :items="options"
                  :label="$t('message.block.form.image.type.label')"
                  v-model="data.type"
              ></v-select>
              <v-text-field
                  :error-messages="errors"
                  :label="$t('message.block.form.image.external.label')"
                  :placeholder="$t('message.block.form.image.external.placeholder')"
                  autofocus
                  v-model="data.src"
                  v-show="data.type === Image.TYPE.EXTERNAL"
              ></v-text-field>
            </v-flex>

            <v-flex class="md6 xs12 order-xs2 order-sm1 pr-3">
              <nx-clipping-form-board-selector
                  name="clipping"
                  :label="$t('message.block.form.image.content.label')"
                  :aspect-ratios="availableAspectRatios"
                  :value="clipping"
                  @input="updateClipping"
                  :allow-non-published="allowNonPublished"
                  v-show="data.type === Image.TYPE.INTERNAL"
              ></nx-clipping-form-board-selector>
              <nx-image
                  :src="data.src || null"
                  v-show="data.type === Image.TYPE.EXTERNAL"
                  @load="previewLoaded(true)"
                  @error="previewLoaded(false)"
              ></nx-image>
            </v-flex>

            <v-flex class="md6 xs12 order-xs1 order-sm2">
              <v-text-field
                  :label="$t('message.block.form.image.author.label')"
                  v-model="data.author"
              ></v-text-field>
              <v-text-field
                  :label="$t('message.block.form.image.caption.label')"
                  v-model="data.caption"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>

  </nx-fullscreen-dialog>
</template>

<script>
import api from '@/modules/clipping/api';
import NxClippingFormBoardSelector from '@/modules/clipping/form/BoardSelector';
import { mapGetters } from 'vuex';
import DialogButton from './DialogButton';
import Image from '../blots/imageEmbed';
import NxImage from '../../../media/Image';
import isEmpty from 'lodash/isEmpty';

/**
 * ImageButton Component
 *
 * Extend DialogButton component
 *
 */
export default {
  name: 'nx-image-button',
  components: { NxImage, NxClippingFormBoardSelector },
  extends: DialogButton,
  props: {
    embedAuthor: String,
    embedCaption: String,
    embedClippingId: String,
    embedSrc: String,
    embedType: String,
    allowNonPublished: Boolean,
    aspectRatios: Array,
  },
  data() {
    return {
      clipping: null,
      data: {
        author: '',
        caption: '',
        clippingId: null,
        src: '',
        type: Image.TYPE.INTERNAL,
      },
      errors: [],
      Image,
      name: 'imageEmbed',
      options: [
        {
          text: this.$t(`message.block.form.image.type.${Image.TYPE.EXTERNAL}`),
          value: Image.TYPE.EXTERNAL,
        },
        {
          text: this.$t(`message.block.form.image.type.${Image.TYPE.INTERNAL}`),
          value: Image.TYPE.INTERNAL,
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['availableClippingAspectRatioInBodyAliases']),
    availableAspectRatios() {
      if (isEmpty(this.aspectRatios)) {
        return this.availableClippingAspectRatioInBodyAliases;
      }
      return this.aspectRatios;
    },
    blockTitle() {
      return this.$t('message.block.photo');
    },
    isValid() {
      if (this.data.type === Image.TYPE.EXTERNAL) {
        return this.data.src !== '' && this.errors.length === 0;
      }
      return !!this.data.clippingId;
    },
  },
  methods: {
    cleanupDialog() {
      this.data = {
        author: '',
        caption: '',
        clippingId: null,
        src: '',
        type: Image.TYPE.EXTERNAL,
      };
    },
    updateClipping(clipping) {
      // Update the caption if it's empty o it's the title of the previously selected clipping
      if (!clipping) {
        this.clipping = null;
        this.data.clippingId = null;
        this.data.caption = null;
        this.data.src = null;
        return;
      }
      if (this.data.caption === ''
        || (this.clipping
          && this.clipping.image
          && this.data.caption === this.clipping.image.title)) {
        this.data.caption = clipping.image.title;
      }
      if (this.data.author === ''
        || (this.clipping
          && this.clipping.image
          && this.data.author === this.clipping.image.author)) {
        this.data.author = clipping.image.author;
      }
      this.clipping = clipping;
      this.data.clippingId = clipping.id;
      this.data.src = clipping.src;
    },
    async fetchClipping() {
      this.clipping = await api.getClipping(this.data.clippingId);
    },
    previewLoaded(withoutErrors) {
      if (withoutErrors) this.errors = [];
      else this.errors.push(this.$t('message.block.form.image.external.error'));
    },
  },
  mounted() {
    this.data = {
      author: this.embedAuthor || '',
      caption: this.embedCaption || '',
      clippingId: this.embedClippingId || '',
      src: this.embedSrc || '',
      type: this.embedType || Image.TYPE.INTERNAL,
    };
    if (this.data.type === Image.TYPE.INTERNAL
      && this.data.clippingId !== '') {
      this.fetchClipping();
    }
  },
};
</script>

<style lang="stylus">
#image-preview {
  img {
    display: table;
    margin: auto;
  }
}
</style>
